import type {
  MeetingTranscriptionInputDevicesPayload,
  MeetingTranscriptionInputSelection,
} from '@electron/shared/meetings.types';

export type PreferredInputDevice = MeetingTranscriptionInputSelection;

export const SYSTEM_DEFAULT_PREFERRED_INPUT_DEVICE: PreferredInputDevice = {
  type: 'system_default',
};

export function normalizePreferredInputDevice(
  value: unknown
): PreferredInputDevice {
  if (
    value &&
    typeof value === 'object' &&
    'type' in value &&
    value.type === 'specific_device' &&
    'deviceId' in value &&
    typeof value.deviceId === 'string' &&
    value.deviceId.length > 0
  ) {
    return { type: 'specific_device', deviceId: value.deviceId };
  }

  return SYSTEM_DEFAULT_PREFERRED_INPUT_DEVICE;
}

export function resolvePreferredInputDeviceId(
  preferredInputDevice: PreferredInputDevice,
  devicesState: MeetingTranscriptionInputDevicesPayload | null | undefined
): string | null {
  if (!devicesState) return null;

  if (preferredInputDevice.type !== 'specific_device') {
    return null;
  }

  return devicesState.availableDevices.some(
    (device) => device.id === preferredInputDevice.deviceId
  )
    ? preferredInputDevice.deviceId
    : null;
}

function isFallingBackToSystemDefault(
  preferredInputDevice: PreferredInputDevice,
  devicesState: MeetingTranscriptionInputDevicesPayload | null | undefined
): boolean {
  if (!devicesState) return false;
  if (preferredInputDevice.type === 'system_default') return true;

  return (
    resolvePreferredInputDeviceId(preferredInputDevice, devicesState) === null
  );
}

/**
 * Label for a saved preference (the `saved_preference` display mode).
 * Resolves the preferred device by id when present in availableDevices, otherwise
 * shows the system-default label.
 */
export function getPreferredInputDeviceDisplayName(
  preferredInputDevice: PreferredInputDevice,
  devicesState: MeetingTranscriptionInputDevicesPayload | null | undefined,
  options?: {
    loadingLabel?: string;
    systemDefaultLabel?: string;
  }
): string {
  const loadingLabel = options?.loadingLabel ?? '—';
  const systemDefaultLabel = options?.systemDefaultLabel ?? 'System Default';

  if (!devicesState) return loadingLabel;

  const resolvedDeviceId = resolvePreferredInputDeviceId(
    preferredInputDevice,
    devicesState
  );
  if (!resolvedDeviceId) {
    return systemDefaultLabel;
  }

  return (
    devicesState.availableDevices.find(
      (device) => device.id === resolvedDeviceId
    )?.name ?? systemDefaultLabel
  );
}

/**
 * Label for the device actually capturing audio right now (the `live_effective`
 * display mode). Reads `effectiveCurrentDevice` so a pinned-but-unavailable
 * selection shows the system-default fallback the user is hearing.
 */
export function getEffectiveInputDeviceDisplayName(
  preferredInputDevice: PreferredInputDevice,
  devicesState: MeetingTranscriptionInputDevicesPayload | null | undefined,
  options?: {
    loadingLabel?: string;
    systemDefaultLabel?: string;
  }
): string {
  const loadingLabel = options?.loadingLabel ?? '—';
  const systemDefaultLabel = options?.systemDefaultLabel ?? 'System Default';

  if (!devicesState) return loadingLabel;
  if (isFallingBackToSystemDefault(preferredInputDevice, devicesState)) {
    return systemDefaultLabel;
  }

  return devicesState.effectiveCurrentDevice?.name ?? systemDefaultLabel;
}
